import { computed, type Ref } from 'vue'

interface UpdateQuantityPayload<TGroup> {
  group: TGroup
  newQuantity: number
}

interface UseNewOrderModalLayoutBindingsOptions<TGroup> {
  form: Ref<any>
  showingItemOptions: Ref<boolean>
  selectedItem: Ref<any>
  topNotes: Ref<string[]>
  persons: Ref<any[]>
  activePersonIndex: Ref<number>
  isEditMode: Ref<boolean>
  availableTables: Ref<any[]>
  loading: Ref<any>
  error: Ref<any>
  categoryNames: Ref<string[]>
  menuItemsByCategory: Ref<any>
  selectedItems: Ref<any[]>
  ivaEnabled: Ref<boolean>
  ivaRate: Ref<number>
  ivaAmount: Ref<number>
  subtotal: Ref<number>
  deliveryFee: Ref<number>
  currentDiscount: Ref<number>
  finalTotal: Ref<number>
  appliedPromotions: Ref<any[]>
  promotionMessages: Ref<string[]>
  isCalculatingPromotions: Ref<boolean>
  selectedCustomer: Ref<any>
  selectedAddress: Ref<any>
  selectedAddressId: Ref<any>
  isCustomerSelectionValid: Ref<boolean>
  markAsPaid: Ref<boolean>
  selectedPaymentMethod: Ref<'cash' | 'card' | 'digital' | 'other'>
  cashReceived: Ref<number>
  isPaymentValid: Ref<boolean>
  hasItems: Ref<boolean>
  canProcessPayments: Ref<boolean>
  isPosOnlyMode: Ref<boolean>
  modeLoaded: Ref<boolean>
  restaurantSettings: any
  getMenuItemName: (id: number) => string
  getMenuItemCategory: (id: number) => string
  getItemQuantity: (menuItemId: number | string, variantId?: number | string | null) => number
  calculateItemTotal: (item: any) => string
  isItemLocked: (item: any) => boolean
  getVariantPrice: (item: any, variant: any) => number
  getDeliveryAddressFromForm: () => string
  handleAddItemFromModal: (data: any) => void
  handleCreateCustomer: () => void
  handleAddressSelected: (address: any) => void
  addPerson: () => void
  removePersonWithConfirm: (index: number) => void
  updatePersonName: (index: number, name: string) => void
  removeGroupFromPerson: (group: TGroup) => void
  updateItemQuantity: (payload: UpdateQuantityPayload<TGroup>) => void
  selectItem: (menuItem: any) => void
  handleToggleIva: (enabled: boolean) => void
  createOrder: () => Promise<void>
}

export function useNewOrderModalLayoutBindings<TGroup>(options: UseNewOrderModalLayoutBindingsOptions<TGroup>) {
  const sharedLayoutProps = computed(() => ({
    form: options.form.value,
    showingItemOptions: options.showingItemOptions.value,
    selectedItem: options.selectedItem.value,
    topNotes: options.topNotes.value,
    persons: options.persons.value,
    activePersonIndex: options.activePersonIndex.value,
    isEditMode: options.isEditMode.value,
    availableTables: options.availableTables.value,
    loading: options.loading.value,
    error: options.error.value,
    categoryNames: options.categoryNames.value,
    menuItemsByCategory: options.menuItemsByCategory.value,
    selectedItems: options.selectedItems.value,
    ivaEnabled: options.ivaEnabled.value,
    ivaRate: options.ivaRate.value,
    ivaAmount: options.ivaAmount.value,
    subtotal: options.subtotal.value,
    deliveryFee: options.deliveryFee.value,
    currentDiscount: options.currentDiscount.value,
    finalTotal: options.finalTotal.value,
    appliedPromotions: options.appliedPromotions.value,
    promotionMessages: options.promotionMessages.value,
    isCalculatingPromotions: options.isCalculatingPromotions.value,
    selectedCustomer: options.selectedCustomer.value,
    selectedAddress: options.selectedAddress.value,
    selectedAddressId: options.selectedAddressId.value,
    isCustomerSelectionValid: options.isCustomerSelectionValid.value,
    markAsPaid: options.markAsPaid.value,
    selectedPaymentMethod: options.selectedPaymentMethod.value,
    cashReceived: options.cashReceived.value,
    isPaymentValid: options.isPaymentValid.value,
    hasItems: options.hasItems.value,
    canProcessPayments: options.canProcessPayments.value,
    isPosOnlyMode: options.isPosOnlyMode.value,
    modeLoaded: options.modeLoaded.value,
    restaurantSettings: options.restaurantSettings,
    getMenuItemName: options.getMenuItemName,
    getMenuItemCategory: options.getMenuItemCategory,
    getItemQuantity: options.getItemQuantity,
    calculateItemTotal: options.calculateItemTotal,
    isItemLocked: options.isItemLocked,
    getVariantPrice: options.getVariantPrice,
    getDeliveryAddressFromForm: options.getDeliveryAddressFromForm
  }))

  const sharedLayoutListeners = {
    'back-to-menu': () => {
      options.showingItemOptions.value = false
      options.selectedItem.value = null
    },
    'add-item': options.handleAddItemFromModal,
    'create-customer': options.handleCreateCustomer,
    'address-selected': options.handleAddressSelected,
    'update:active-person-index': (index: number) => {
      options.activePersonIndex.value = index
    },
    'add-person': options.addPerson,
    'remove-person': options.removePersonWithConfirm,
    'update-person-name': (data: { index: number; name: string }) => {
      options.updatePersonName(data.index, data.name)
    },
    'remove-group': options.removeGroupFromPerson,
    'update-quantity': (group: TGroup, newQuantity: number) => {
      options.updateItemQuantity({ group, newQuantity })
    },
    'select-item': options.selectItem,
    'toggle-iva': options.handleToggleIva,
    'create-order': options.createOrder,
    'update:form': (value: any) => {
      options.form.value = value
    },
    'update:selected-customer': (value: any) => {
      options.selectedCustomer.value = value
    },
    'update:form-notes': (value: string) => {
      const formWithNotes = options.form.value as { notes?: string }
      formWithNotes.notes = value
    },
    'update:mark-as-paid': (value: boolean) => {
      options.markAsPaid.value = value
    },
    'update:selected-payment-method': (value: 'cash' | 'card' | 'digital' | 'other') => {
      options.selectedPaymentMethod.value = value
    },
    'update:cash-received': (value: number) => {
      options.cashReceived.value = value
    }
  }

  return {
    sharedLayoutProps,
    sharedLayoutListeners
  }
}
